import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Copy, Download, Loader2, Link2Off } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errMsg, downloadFile } from "@/lib/api";

const GoogleCal = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="3" fill="#fff" stroke="#4285F4" strokeWidth="2" /><path d="M3 8h18" stroke="#4285F4" strokeWidth="2" /><text x="12" y="18" textAnchor="middle" fontSize="8" fontWeight="700" fill="#34A853">31</text></svg>
);
const AppleCal = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4" fill="#fff" stroke="#102B40" strokeWidth="1.6" /><path d="M3 9h18" stroke="#EF4444" strokeWidth="3" /><text x="12" y="18.5" textAnchor="middle" fontSize="8" fontWeight="700" fill="#102B40">31</text></svg>
);

const Option = ({ icon, title, sub, onClick, testid, busy }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid} className="flex w-full items-center gap-3 rounded-2xl border border-ww-border bg-white px-4 py-3.5 text-left transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-ww-bright hover:shadow-[0_10px_30px_-14px_rgba(8,124,101,0.5)] disabled:opacity-60">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ww-cyan">{busy ? <Loader2 className="h-5 w-5 animate-spin text-ww-emerald" /> : icon}</span>
    <span><span className="block text-[15px] font-semibold text-ww-navy">{title}</span><span className="block text-xs text-ww-muted">{sub}</span></span>
  </button>
);

export function CalendarExport({ productId, className = "ww-btn-ghost" }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState(null);
  const [busy, setBusy] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    if (!productId) setFeed(await api("get", "/calendar/feed").catch(() => ({ enabled: false })));
  };
  const ensureFeed = async () => feed?.enabled ? feed : (await api("post", "/calendar/feed").then((f) => { setFeed(f); return f; }));
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const google = () => {
    const w = window.open("", "_blank");
    run("google", async () => { const f = await ensureFeed(); if (w) w.location.href = f.google_url; else window.location.href = f.google_url; });
  };
  const apple = () => run("apple", async () => { const f = await ensureFeed(); window.location.href = f.webcal_url; });
  const download = () => run("download", () => downloadFile(api, productId ? `/products/${productId}/calendar.ics` : "/calendar/download.ics", productId ? "expiry-dates.ics" : "warranty-wallet-expiry-dates.ics"));
  const copy = () => run("copy", async () => { const f = await ensureFeed(); await navigator.clipboard.writeText(f.https_url); toast.success("Calendar link copied"); });
  const revoke = () => run("revoke", async () => { setFeed(await api("delete", "/calendar/feed")); toast.success("Calendar link turned off. Subscribed calendars will stop updating."); });

  return (
    <>
      <button onClick={openDialog} className={className} data-testid={productId ? "product-calendar-button" : "wallet-calendar-button"}><CalendarPlus className="h-4 w-4" />Add to Calendar</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-[26px] p-7" data-testid="calendar-export-dialog">
          <DialogTitle className="font-display text-xl text-ww-navy">Add expiry dates to your calendar</DialogTitle>
          <DialogDescription>
            {productId ? "Download this product's expiry dates as all-day events, with alerts 7 days and 1 day before." : "Subscribe once, and every warranty and guarantee expiry date appears in your calendar. New and edited products sync automatically."}
          </DialogDescription>
          <div className="mt-2 space-y-3">
            {!productId && (
              <>
                <Option testid="calendar-google-button" icon={<GoogleCal />} title="Add to Google Calendar" sub="Subscribe — keeps updating automatically" onClick={google} busy={busy === "google"} />
                <Option testid="calendar-apple-button" icon={<AppleCal />} title="Add to Apple Calendar" sub="iPhone, iPad and Mac — keeps updating" onClick={apple} busy={busy === "apple"} />
              </>
            )}
            <Option testid="calendar-download-button" icon={<Download className="h-5 w-5 text-ww-emerald" />} title="Download .ics file" sub="Works with Outlook, Apple and Google — one-time import" onClick={download} busy={busy === "download"} />
          </div>
          {!productId && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-ww-border pt-4 text-xs text-ww-muted">
              <button onClick={copy} className="flex items-center gap-1.5 font-semibold text-ww-emerald" data-testid="calendar-copy-link"><Copy className="h-3.5 w-3.5" />Copy private link</button>
              {feed?.enabled && <button onClick={revoke} className="flex items-center gap-1.5 font-semibold text-red-600" data-testid="calendar-revoke-link"><Link2Off className="h-3.5 w-3.5" />Turn off link</button>}
              <p className="w-full">The private link shows only product names, coverage names and dates — never your documents. Anyone with the link can see them, so keep it private; you can turn it off anytime.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
